import { useMemo, useState, useRef, useEffect } from "react";
import { Search, X, ArrowRight } from "lucide-react";

// Search across every variety + region and jump to its family on the timeline.
export default function VarietySearch({ families, onPick }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const index = useMemo(() => {
    const rows = [];
    (families || []).forEach((f) => {
      f.varieties.forEach((v) => {
        rows.push({
          familySlug: f.slug,
          familyName: f.name,
          name: v.name,
          region: v.region,
          origin_type: v.origin_type,
          key: `${v.name}__${v.region}`,
          haystack: `${v.name} ${v.region} ${f.name}`.toLowerCase(),
        });
      });
    });
    return rows;
  }, [families]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return index.filter((r) => r.haystack.includes(q)).slice(0, 8);
  }, [query, index]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (r) => {
    onPick(r.familySlug, r.key);
    setQuery(r.name);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative w-full max-w-md" data-testid="variety-search">
      <div className="flex items-center gap-3 rounded-full border border-border bg-cream-100 px-5 py-3 transition-colors duration-300 focus-within:border-gold">
        <Search className="h-4 w-4 flex-none text-forest-muted" />
        <input
          data-testid="variety-search-input"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search any variety or region…"
          className="w-full bg-transparent font-sans text-sm text-forest placeholder:text-forest-muted/70 focus:outline-none"
        />
        {query && (
          <button
            data-testid="variety-search-clear"
            onClick={() => { setQuery(""); setOpen(false); }}
            className="flex-none text-forest-muted hover:text-forest"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && query.trim() && (
        <div
          data-testid="variety-search-results"
          className="absolute z-30 mt-2 w-full overflow-hidden rounded-sm border border-border bg-cream-100 shadow-[0_18px_40px_rgba(46,74,46,0.14)]"
        >
          {matches.length === 0 ? (
            <div className="px-5 py-4 font-mono text-xs uppercase tracking-wider text-forest-muted">
              No varieties match “{query}”
            </div>
          ) : (
            matches.map((r) => (
              <button
                key={`${r.familySlug}-${r.key}`}
                data-testid={`variety-search-result-${r.familySlug}`}
                onClick={() => pick(r)}
                className="group flex w-full items-center justify-between gap-3 border-b border-border/60 px-5 py-3 text-left transition-colors duration-200 last:border-0 hover:bg-cream-200"
              >
                <span className="min-w-0">
                  <span className="block truncate font-display text-lg leading-tight text-forest">{r.name}</span>
                  <span className="block truncate font-mono text-[0.62rem] uppercase tracking-wide text-forest-muted">
                    {r.familyName} · {r.region}
                  </span>
                </span>
                <span className="flex flex-none items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${r.origin_type === "indian" ? "bg-harvest" : "bg-terracotta"}`} />
                  <ArrowRight className="h-4 w-4 text-forest-muted transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-gold" />
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
